import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Grid from "../../atoms/Grid/Grid";
import { endpoints } from "../../../api/endpoints";
import {
  getCustomerLoginAs,
  getQuoteListByParameters,
} from "../../../store/slices/CustomerSlice/CustomerSlice";

const quoteHeaderAttributes = [
  {
    id: "quoteNumber",
    label: "Quote #",
    tooltip: "Quote Number",
    sortable: true,
    searchable: {
      label: "Search by Quote Number",
      type: "text",
      width: 2,
    },
    width: "10%",
  },
  {
    id: "po",
    label: "PO",
    sortable: false,
    width: "8%",
  },
  {
    id: "dateOfQuote",
    label: "Date",
    tooltip: "Date of Quote",
    sortable: true,
    width: "10%",
  },
  {
    id: "total",
    label: "Total",
    sortable: true,
    width: "10%",
  },
  {
    id: "agent",
    label: "Agent",
    sortable: true,
    width: "10%",
  },
  {
    id: "warehouseName",
    label: "Wh",
    tooltip: "Warehouse Name",
    sortable: true,
    width: "8%",
  },
  {
    id: "customer",
    label: "Customer",
    sortable: true,
    searchable: {
      label: "Search by Customer Name",
      type: "text",
      width: 2,
    },
    width: "15%",
  },
  {
    id: "dropShipAddress",
    label: "Drop Ship Address",
    sortable: false,
    width: "15%",
  },
  {
    id: "comments",
    label: "Comments",
    sortable: false,
    width: "12%",
  },
  {
    id: "details",
    label: "Details",
    sortable: false,
    width: "8%",
  },
];

const returnRealAttributeName = (attr) => {
  switch (attr) {
    case "quoteNumber":
      return "quote_num";
    case "dateOfQuote":
      return "date_of_quote";
    case "customer":
      return "customer_name";
    case "warehouseName":
      return "warehouse_id";
    default:
      return attr;
  }
};

const fetchQuoteData = async (dispatch, params) => {
  const quoteParams = {
    quoteId: params.quoteNumber,
    page: params.page,
    customerNum: params.customerNumbers, // This will be comma-delimited string
    customerName: params.customer,
    orderBy: returnRealAttributeName(params.sortBy),
    sortOrder: params.sortOrder === "desc" ? "dsc" : "asc",
    from: params.from,
    to: params.to,
  };

  const result = await dispatch(getQuoteListByParameters(quoteParams));

  if (!result?.error) {
    return {
      count: result.payload.count,
      results: result.payload.results,
    };
  }

  return { count: 0, results: [] };
};

const mapQuoteData = (quotes) => {
  return quotes.map((d) => ({
    id: d.quote_num,
    quoteNumber: (
      <Link
        to={`/quote-detail/${d.quote_num}`}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {d.quote_num}
      </Link>
    ),
    po: d?.customer_po || "-",
    dateOfQuote: d.date_of_quote
      ? dayjs(d.date_of_quote).format("MM/DD/YYYY")
      : "-",
    total: d.total ? `$${parseFloat(d.total).toFixed(2)}` : "$0.00",
    agent: d.took_by || "-",
    warehouseName: d.warehouse_name || "-",
    customer: d.customer_name ? (
      <Link
        to={`${endpoints.ORDER_ENTRY}/customers/${d.customer_id}`}
        className="text-[var(--primary-color)] hover:text-[var(--primary-color-dark)] underline"
      >
        {`${d.customer_name} | ${d.customer_id}`}
      </Link>
    ) : (
      <span>-</span>
    ),
    dropShipAddress: d?.freight?.drop_ship_address?.drop_ship_address_id ? (
      <div className="text-sm">
        <div>{d.freight.drop_ship_address.ship_to_name}</div>
        <div className="text-gray-600">
          {d.freight.drop_ship_address.city} {d.freight.drop_ship_address.state}
        </div>
      </div>
    ) : (
      <span className="text-gray-500">N/A</span>
    ),
    comments:
      d?.freight?.comments?.length > 0 ? (
        <div className="text-sm max-w-xs overflow-hidden">
          <span className="truncate block" title={d.freight.comments.join(" ")}>
            {d.freight.comments.join(" ")}
          </span>
        </div>
      ) : (
        <span>-</span>
      ),
    details: (
      <Link
        to={`/quote-detail/${d.quote_num}`}
        target="_blank"
        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-[var(--primary-color)] hover:bg-[var(--primary-color-dark)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)]"
      >
        View
      </Link>
    ),
  }));
};

export default function QuoteHistory() {
  const dispatch = useDispatch();
  const { loginOptions } = useSelector((state) => state.customer);

  // Fetch customer list on component mount
  useEffect(() => {
    dispatch(getCustomerLoginAs());
  }, [dispatch]);

  // Transform loginOptions to dropdown options
  const customerOptions =
    loginOptions?.map((customer) => ({
      id: customer.id.toString(),
      value: customer.id.toString(),
      label: `${customer.id} - ${customer.label}`,
      customerName: customer.label,
    })) || [];

  const customFilters = [
    {
      id: "customerNumbers",
      label: "Filter by Customer Numbers",
      placeholder: "Select customer numbers...",
      type: "autocomplete-multiselect",
      options: customerOptions,
    },
  ];

  return (
    <div className="p-6">
      <div className="mt-6">
        <Grid
          title="Quote History"
          headerAttributes={quoteHeaderAttributes}
          fetchDataFunction={fetchQuoteData}
          mapDataFunction={mapQuoteData}
          loadingSelector={(state) => state.loaderSpinner?.loading}
          rowsPerPage={50}
          defaultSortBy="quoteNumber"
          defaultSortOrder="desc"
          showFilters={true}
          showDateFilter={true}
          showPagination={true}
          customFilters={customFilters}
        />
      </div>
    </div>
  );
}
